import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, FileText, CreditCard, Calendar, TrendingUp, Building2, 
  Baby, MapPin, ArrowLeft, Lock, BarChart3, PieChart
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminLogin({ onLogin }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { code });
      onLogin(res.data.token);
    } catch {
      setError('Invalid admin code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-500/30">
            <Building2 className="h-8 w-8 text-blue-400" />
          </div>
          <h1 className="text-3xl font-black text-white">SHALEM Admin</h1>
          <p className="text-blue-300 mt-2">City Management Dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-blue-200 font-medium block mb-2">
                <Lock className="inline h-4 w-4 mr-1" /> Admin Code
              </label>
              <Input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-12 rounded-xl"
                placeholder="Enter admin code"
                data-testid="admin-code-input"
              />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-xl font-bold text-base"
              data-testid="admin-login-btn"
            >
              {loading ? 'Connecting...' : 'Access Dashboard'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'blue', large = false }) {
  return (
    <Card className={`p-6 bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl ${large ? 'col-span-2' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-blue-300 font-medium">{label}</p>
          <p className={`${large ? 'text-4xl' : 'text-2xl'} font-black text-white mt-1`}>{value}</p>
          {sub && <p className="text-xs text-blue-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-${color}-500/20`}>
          <Icon className={`h-6 w-6 text-${color}-400`} />
        </div>
      </div>
    </Card>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/stats`)
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
      </div>
    );
  }

  const categoryColors = {
    water: '#3b82f6', electricity: '#f59e0b', arnona: '#8b5cf6',
    fines: '#ef4444', education: '#10b981', health: '#ec4899', general: '#6366f1'
  };

  const categoryNames = {
    water: 'Water', electricity: 'Electricity', arnona: 'Arnona',
    fines: 'Fines', education: 'Education', health: 'Health', general: 'General'
  };

  const maxCatTotal = stats?.categories ? Math.max(...stats.categories.map(c => c.total)) : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white flex items-center gap-3">
              <Building2 className="h-8 w-8 text-blue-400" />
              SHALEM Admin
            </h1>
            <p className="text-blue-300 mt-1">City Management Dashboard</p>
          </div>
          <div className="flex gap-3">
            <a href="/" className="text-blue-300 hover:text-white transition-colors flex items-center gap-2 text-sm">
              <ArrowLeft className="h-4 w-4" /> Back to App
            </a>
            <Button onClick={onLogout} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 rounded-xl">
              Logout
            </Button>
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Total Citizens" value={stats?.total_users || 0} sub="Registered accounts" />
          <StatCard icon={FileText} label="Total Bills" value={stats?.total_bills || 0} sub={`${stats?.unpaid_bills || 0} unpaid`} />
          <StatCard icon={CreditCard} label="Amount Collected" value={`₪${(stats?.collected_amount || 0).toFixed(0)}`} sub="Total payments" color="green" />
          <StatCard icon={TrendingUp} label="Collection Rate" value={`${stats?.collection_rate || 0}%`} sub="Bills paid" color="green" />
        </div>

        {/* Financial Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard icon={FileText} label="Unpaid Amount" value={`₪${(stats?.unpaid_amount || 0).toFixed(0)}`} sub={`${stats?.unpaid_bills || 0} bills pending`} color="red" large />
          <Card className="p-6 bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="h-5 w-5 text-blue-400" />
              <h3 className="text-sm font-semibold text-blue-300">Services Usage</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-blue-200">Appointments</span>
                <span className="text-white font-bold">{stats?.total_appointments || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-200">Address Changes</span>
                <span className="text-white font-bold">{stats?.total_address_changes || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-200">Children Registered</span>
                <span className="text-white font-bold">{stats?.total_children || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-blue-200">Payments Made</span>
                <span className="text-white font-bold">{stats?.total_payments || 0}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Categories Breakdown */}
        <Card className="p-6 bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl mb-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="h-5 w-5 text-blue-400" />
            <h3 className="text-lg font-bold text-white">Unpaid Bills by Category</h3>
          </div>
          <div className="space-y-4">
            {(stats?.categories || []).sort((a, b) => b.total - a.total).map((cat, idx) => (
              <div key={idx}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-200">{categoryNames[cat.category] || cat.category}</span>
                  <span className="text-white font-bold">₪{cat.total.toFixed(0)} ({cat.count} bills)</span>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${(cat.total / maxCatTotal) * 100}%`,
                      background: categoryColors[cat.category] || '#6366f1'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Users */}
        <Card className="p-6 bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-400" />
            Recent Registrations
          </h3>
          <div className="space-y-3">
            {(stats?.recent_users || []).map((user, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div>
                  <p className="text-white font-medium">{user.full_name}</p>
                  <p className="text-blue-300 text-xs">ID: {user.teoudat_zehout}</p>
                </div>
                <p className="text-blue-400 text-xs">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  const handleLogin = (t) => {
    setToken(t);
    localStorage.setItem('admin_token', t);
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('admin_token');
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
